import {useCallback, useState, ChangeEvent, FormEvent} from 'react';
import {AxiosError} from 'axios';
import {useMutation} from 'react-query';
import {GenericErrorType} from '../types';

interface UseFormStateOptions<ResponseType> {
  onSuccess?: (data: ResponseType) => void;
  onError?: (data: AxiosError) => void;
}
export interface GenericValidationErrorType<T> {
  readonly errors: T;
}

export type ErrorType<T> = T extends object
  ? {[L in keyof T]?: ErrorType<T[L]>}
  : string;

interface UseFormStateType<
  RequestType,
  ResponseType,
  ErrorResponseType = ErrorType<RequestType>,
> {
  formFields: RequestType;
  setFormFields: React.Dispatch<React.SetStateAction<RequestType>>;
  handleChange: (
    e: ChangeEvent<HTMLInputElement | HTMLTextAreaElement | HTMLSelectElement>,
  ) => void;
  handleSubmit: (e?: FormEvent) => void;
  handlePayloadSubmit: (payload: RequestType) => void;
  isLoading: boolean;
  isSuccess: boolean;
  responseData: ResponseType;
  fieldErrors: ErrorResponseType;
  genericError: string | null;
}

export default function useFormState<
  RequestType,
  ResponseType,
  ErrorResponseType = ErrorType<RequestType>,
>(
  submitAsync: (values: RequestType) => Promise<ResponseType>,
  initialValues?: RequestType,
  options?: UseFormStateOptions<ResponseType>,
): UseFormStateType<RequestType, ResponseType, ErrorResponseType> {
  const [formFields, setFormFields] = useState<RequestType>(initialValues);
  const [fieldErrors, setFieldErrors] = useState<ErrorResponseType>(null);
  const [genericError, setGenericError] = useState<string>(null);
  const [responseData, setResponseData] = useState<ResponseType>(null);

  const handleChange = useCallback(
    (e: ChangeEvent<HTMLInputElement | HTMLTextAreaElement>) => {
      const {name, value} = e.currentTarget;
      setFormFields((prevState) => ({...prevState, [name]: value}));
    },
    [],
  );

  const {
    mutate: handlePayloadSubmit,
    isLoading,
    isSuccess,
  } = useMutation<ResponseType, AxiosError, RequestType>(submitAsync, {
    onSuccess: (data) => {
      setResponseData(data);
      setFieldErrors(null);
      setGenericError(null);
      options?.onSuccess?.(data);
    },
    onError: (error: AxiosError) => {
      if (error.response.status === 400) {
        const {
          response: {data},
        } = error as AxiosError<GenericValidationErrorType<ErrorResponseType>>;
        setFieldErrors(data.errors);
      } else {
        const {
          response: {data},
        } = error as AxiosError<GenericErrorType>;
        setGenericError(data.detail);
      }
      options?.onError?.(error);
    },
  });

  const handleSubmit = useCallback(
    (e?: FormEvent) => {
      if (e) e.preventDefault();
      handlePayloadSubmit(formFields);
    },
    [handlePayloadSubmit, formFields],
  );

  return {
    formFields,
    setFormFields,
    handleChange,
    handleSubmit,
    handlePayloadSubmit,
    isLoading,
    isSuccess,
    responseData,
    fieldErrors,
    genericError,
  };
}
